import { useCallback, useEffect, useRef, useState } from "react";
import MainLayout from "../../components/layout";
import DashboardGrid, { AppCard, SessionsMap } from "../../components/dashboard";
import {
  APP_DETAILS,
  APP_TO_SUBSCRIPTIONS_MAPPING,
  AppId,
  Subscriptions,
  SUBSCRIPTION_NAME_MAP,
} from "@dashboard/constants/appRegistry";
import LoadingSpinner from "../../components/modal";
import PWAInfoPopup from "../../components/modal/PWAInfoPopup";
import { useAuth, useSessionSocket } from "@workspace/ui/hooks";
import { useIsOnline } from "@dashboard/features/online";
import { authServices, profileServices, selectedAppStore } from "@workspace/shared";

export const Dashboard = () => {
  const { setSelectedAppState } = selectedAppStore.getState();
  const [appsBySubscription, setAppsBySubscription] = useState<Partial<Record<AppId, AppCard>>>({});
  const [currentSessions, setCurrentSessions] = useState<SessionsMap>({});
  const [loading, setLoading] = useState(true);
  // No subscriptions from the API AND none cached on this device: the
  // products are unknown, not missing (as in-game). Greying every tile would
  // invite a paying team to "Request Access" to what it already has.
  const [subscriptionsUnknown, setSubscriptionsUnknown] = useState(false);
  // The spinner is for the first load only: a refetch (back online, say)
  // keeps the tiles — and any dialog open over them — on screen.
  const hasFetchedRef = useRef(false);
  const [showInstallMessage, setShowInstallMessage] = useState(false);
  const { isAuthenticated } = useAuth();
  const { isOnline } = useIsOnline();

  // Live session updates: a takeover/admin revoke emits `session:released`
  // (hard redirect, in-game parity); any register/release anywhere emits
  // `session:changed` → refetch the tile locks in real time, no reload.
  // fetchSessions is defined below — reach it through a ref.
  const fetchSessionsRef = useRef<(() => Promise<void>) | null>(null);
  useSessionSocket({
    onSessionsChanged: () => {
      void fetchSessionsRef.current?.();
    },
  });

  const isIosSafari = (): boolean => {
    const ua = window.navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(ua);
    // iPadOS Safari masquerades as a desktop Mac in its user agent; a real
    // laptop Mac has no touch points, so this only matches actual iPads —
    // the install prompt must NOT show in laptop browsers.
    const isIpadMasqueradingAsMac =
      /macintosh|mac os x/.test(ua) && navigator.maxTouchPoints > 1;
    const isSafari =
      ua.includes("safari") &&
      !ua.includes("crios") &&
      !ua.includes("fxios") &&
      !ua.includes("chrome") &&
      !ua.includes("chromium") &&
      !ua.includes("edg") &&
      !ua.includes("firefox");
    return (isIOS || isIpadMasqueradingAsMac) && isSafari;
  };

  const isInStandaloneMode = (): boolean => {
    return (
      window.matchMedia("(display-mode: standalone)").matches ||
      ("standalone" in navigator && (navigator as any).standalone)
    );
  };

  const validateSessionAndGetApps = useCallback(async () => {
    const readCache = (): Subscriptions[] | null => {
      const cached = localStorage.getItem("cachedSubscriptions");
      if (!cached) return null;
      try {
        return JSON.parse(cached) as Subscriptions[];
      } catch (cacheError) {
        console.warn("Failed to parse cached subscriptions:", cacheError);
        return null;
      }
    };
    let validSubscriptions: Subscriptions[] | null = null;
    try {
      if (isOnline) {
        setSelectedAppState({ key: "appKey", value: "Dashboard" });
        try {
          const { data: subscriptionResponse } = await profileServices.onGetSubscriptions();
          validSubscriptions = subscriptionResponse
            .map((sub: any) => {
              const apiSubType = typeof sub === "string" ? sub : sub?.type;
              if (!apiSubType) return null;
              const normalized = SUBSCRIPTION_NAME_MAP[String(apiSubType).toLowerCase()];
              if (!normalized) return null;
              return normalized;
            })
            .filter((sub): sub is Subscriptions => sub !== null);

          localStorage.setItem("cachedSubscriptions", JSON.stringify(validSubscriptions));
        } catch (fetchError) {
          console.warn("Failed to fetch subscriptions while online, using cache:", fetchError);
          validSubscriptions = readCache();
        }
      } else {
        validSubscriptions = readCache();
      }
    } catch (error: any) {
      console.error("Failed to validate session and get apps:", error);
      validSubscriptions = readCache();
    }

    const appCardMap: Partial<Record<AppId, AppCard>> = {};
    for (const [appId, subscriptionList] of Object.entries(APP_TO_SUBSCRIPTIONS_MAPPING)) {
      const matchedCount = (validSubscriptions ?? []).reduce((count, subType) => {
        return subscriptionList.includes(subType) ? count + 1 : count;
      }, 0);
      if (matchedCount > 0) {
        const app = APP_DETAILS[appId as Exclude<AppId, AppId.Dashboard>];
        appCardMap[appId as AppId] = { apps: [app], count: matchedCount };
      }
    }
    setAppsBySubscription(appCardMap);
    // Known: from the API, or cached on this device. Neither: unknown.
    setSubscriptionsUnknown(validSubscriptions === null);
    setLoading(false);
  }, [isOnline, setSelectedAppState]);

  const fetchSessions = useCallback(async () => {
    if (!isOnline) {
      setCurrentSessions({});
      return;
    }
    try {
      const { data } = await authServices.onGetCurrentSessions();
      setCurrentSessions((data ?? {}) as SessionsMap);
    } catch (error) {
      console.warn("Failed to fetch current sessions", error);
      setCurrentSessions({});
    }
  }, [isOnline]);
  fetchSessionsRef.current = fetchSessions;

  // In-game parity ("syncSessions"): arriving at the dashboard FIRST releases
  // every session row this device holds (POST /auth/logout under the
  // Dashboard app key — session rows only, auth tokens untouched), THEN
  // fetches the fresh map. This is what frees an app's slot when the coach
  // leaves it for the dashboard / switches apps — other devices unlock.
  const syncSessions = useCallback(async () => {
    if (!isOnline) {
      setCurrentSessions({});
      return;
    }
    try {
      await authServices.onReleaseDeviceSessions();
    } catch (error) {
      console.warn("Failed to release this device's sessions", error);
    }
    await fetchSessions();
  }, [isOnline, fetchSessions]);

  useEffect(() => {
    if (!isAuthenticated) {
      hasFetchedRef.current = false;
      return;
    }
    if (!hasFetchedRef.current) setLoading(true);
    hasFetchedRef.current = true;
    void validateSessionAndGetApps();
    void syncSessions();
  }, [isAuthenticated, validateSessionAndGetApps, syncSessions]);

  // Coming back to the dashboard tab (from another window or the iPad home
  // screen) re-checks the locks. Fetch-only — releasing sessions belongs to
  // the mount path above, so a background tab regaining focus can't revoke
  // an app open in another tab of this same device.
  useEffect(() => {
    if (!isAuthenticated) return;
    const onFocus = () => void fetchSessions();
    const onVisibility = () => {
      if (document.visibilityState === "visible") void fetchSessions();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [isAuthenticated, fetchSessions]);

  useEffect(() => {
    if (!isAuthenticated) {
      setShowInstallMessage(false);
      return;
    }

    if (isIosSafari() && !isInStandaloneMode()) {
      setShowInstallMessage(true);
    } else {
      setShowInstallMessage(false);
    }
  }, [isAuthenticated]);

  return (
    <>
      {showInstallMessage && <PWAInfoPopup />}
      <MainLayout showHeader={true} variant="centered">
        {/* The product picker (client 2026-11-04 mockups, as in-game). */}
        <div className="flex flex-1 flex-col items-center justify-center py-8 md:py-12">
          <h1 className="text-balance text-center text-[28px] font-bold leading-tight tracking-[-0.01em] text-[#111827] md:text-[36px]">
            Need to log in?
          </h1>
          <p className="mt-3 max-w-[250px] text-balance text-center text-[16px] leading-snug text-[#1F2937] md:max-w-none md:text-[17px]">
            Select a product icon below to access your account.
          </p>
          <div className="mt-10 md:mt-12">
            {loading ? (
              <LoadingSpinner />
            ) : subscriptionsUnknown ? (
              <div role="status" className="max-w-[296px] text-center">
                <p className="text-[15px] leading-snug text-[#374151]">
                  We couldn&rsquo;t load your products. Check your connection and try again.
                </p>
                <button
                  type="button"
                  className="mt-4 rounded-md bg-blue-600 px-3.5 py-2 font-semibold text-white transition hover:bg-blue-700"
                  onClick={() => {
                    setLoading(true);
                    void validateSessionAndGetApps();
                  }}
                >
                  Try again
                </button>
              </div>
            ) : (
              <DashboardGrid
                appsBySubscription={appsBySubscription}
                currentSessions={currentSessions}
                onSessionsRefresh={syncSessions}
              />
            )}
          </div>
          <p className="mt-14 text-center text-[15px] leading-relaxed text-[#4B5563] md:mt-16">
            Need help? Contact{" "}
            <a href="mailto:support@modernfootball.com" className="block text-[#1D4ED8] hover:underline md:inline">
              support@modernfootball.com
            </a>
          </p>
        </div>
      </MainLayout>
    </>
  );
};
